import { useEffect, useRef, useState, useCallback } from 'react'
import { Album, AlbumPreviewFile } from '@/models/album.ts'
import {
  IconPhoto,
  IconChevronDown,
  IconChevronRight,
  IconFileDescription,
  IconTrash,
  IconDeviceDesktop,
  IconDeviceMobile,
  IconPhotoCheck,
  IconDownload,
  IconBoxMultiple,
  IconFileText,
  IconFocusCentered,
  IconX,
  IconUpload,
  IconPhotoPlus,
  IconArrowsMove,
  IconCheck,
  IconZoomIn,
  IconZoomOut,
  IconArrowUp,
  IconArrowDown,
} from '@tabler/icons-react'
import { Loading, Modal, Textarea } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { useClient } from '@/stores/useClient.ts'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { FileFilterProvider } from '@/contexts/fileFilter.tsx'
import { FilesProvider } from '@/contexts/filesContext.tsx'
import { useFilesContext } from '@/contexts/filesContext.tsx'
import FilesListing from '@/components/Files/FilesListing.tsx'
import FilesSlideshow from '@/components/Files/FilesSlideshow.tsx'
import { AlbumFile } from '@/models/albumFile.ts'
import { AlbumFileType } from '@/models/albumFileType'
import InlineEditInput from '@/components/portal/InlineEditInput.tsx'
import { DeleteConfirmModal, useDeleteConfirmModal } from '@/components/portal/DeleteConfirmModal.tsx'
import { useAlbums } from '@/stores/useAlbums.ts'
import useFileSelection from '@/hooks/useFileSelection.tsx'
import { getFilterFromSelection } from '@/hooks/useFileSelection.tsx'
import { FileAction } from '@/components/Files/FileHoverOverlay.tsx'
import { SimpleActionsModal, SimpleAction } from '@/components/Files/FileActionsModal.tsx'
import FocalPointModal from '@/components/Files/FocalPointModal.tsx'
import useLongPress from '@/hooks/useLongPress.ts'
import { saveAs } from '@/controller/fileSaver.ts'
import { useUploadQueue, isStatusFinalState } from '@/stores/useUploadQueue.ts'
import { useDrives } from '@/stores/useDrives.ts'
import { makeKey } from '@/stores/useFiles.ts'
import useMultiDownload from '@/hooks/useMultiDownload.ts'
import DownloadQualityOptions from '@/components/Files/DownloadQualityOptions.tsx'
import DownloadProgressBar from '@/components/Files/DownloadProgressBar.tsx'
import { ALL_DOWNLOAD_QUALITIES, DownloadQuality } from '@/models/downloadOptions.ts'
import BulkMoveModal from '@/components/BulkMoveModal.tsx'
import TutorialSpot from '@/components/tutorial/TutorialSpot.tsx'

interface AlbumCardProps {
  album: Album & { files_count?: number }
  projectId: string
  /** Move one position up/down in the drive; undefined = already at that end (button disabled). */
  onMoveUp?: () => void
  onMoveDown?: () => void
  isReordering?: boolean
}

export default function AlbumCard({ album, projectId, onMoveUp, onMoveDown, isReordering }: AlbumCardProps) {
  const { updateAlbum, deleteAlbum, applyFileDeclaration } = useClient()
  const queryClient = useQueryClient()
  const { setAlbum } = useAlbums()
  const [isExpanded, setIsExpanded] = useState(false)
  const [isDeletingAlbum, setIsDeletingAlbum] = useState(false)
  const deleteModal = useDeleteConfirmModal()
  const descriptionModalRef = useRef<HTMLDialogElement>(null)
  const [descriptionValue, setDescriptionValue] = useState(album.description)
  const [isSavingDescription, setIsSavingDescription] = useState(false)
  const [isApplyingExamples, setIsApplyingExamples] = useState(false)
  const albumIsEmpty = album.files_count === 0

  // Upload state
  const inputFileRef = useRef<HTMLInputElement>(null)
  const { uploadQueue, registerFileUploads } = useUploadQueue()
  const albumUploads = uploadQueue.filter(f => f.album.id === album.id)
  const isUploading = albumUploads.some(f => !isStatusFinalState(f.status))

  // Refresh project data (including files_count) when uploads finish
  const wasUploadingRef = useRef(false)
  useEffect(() => {
    if (isUploading) {
      wasUploadingRef.current = true
    } else if (wasUploadingRef.current) {
      wasUploadingRef.current = false
      invalidateProject()
    }
  }, [isUploading])

  const handleUploadClick = useCallback(() => {
    if (!isUploading) {
      inputFileRef.current?.click()
    }
  }, [isUploading])

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files
    if (!selectedFiles || selectedFiles.length === 0) return
    const fileArray = Array.from(selectedFiles).map(file => ({ file }))
    registerFileUploads(album, fileArray)
    setIsExpanded(true)
    // Reset input so the same files can be selected again
    e.target.value = ''
  }, [album, registerFileUploads])

  // Focal point modal state (shared between preview tiles and gallery hover actions)
  const focalPointModalRef = useRef<HTMLDialogElement>(null)
  const [focalPointImageUrl, setFocalPointImageUrl] = useState<string | null>(null)
  const [focalPointNewFileId, setFocalPointNewFileId] = useState<string | null>(null)
  const [focalPointType, setFocalPointType] = useState<'desktop' | 'mobile'>('desktop')
  const [focalPointInitial, setFocalPointInitial] = useState({ x: 0.5, y: 0.5 })
  const [isSavingFocalPoint, setIsSavingFocalPoint] = useState(false)

  function invalidateProject() {
    queryClient.invalidateQueries({ queryKey: ['project', projectId] })
  }

  async function handleApplyExamplesFromTile() {
    setIsApplyingExamples(true)
    try {
      await applyFileDeclaration(album.id, 'prof')
      invalidateProject()
      queryClient.invalidateQueries({ queryKey: ['album', album.id, 'files'] })
      toast('Beispiel-Bilder wurden geladen', { closeButton: true, autoClose: 2000 })
    } catch {
      toast('Fehler beim Laden der Beispiel-Bilder', { closeButton: true, autoClose: 3000, type: 'error' })
    } finally {
      setIsApplyingExamples(false)
    }
  }

  async function handleTitleSave(newTitle: string) {
    await updateAlbum(album.id, { title: newTitle } as any)
    invalidateProject()
    toast('Albumname aktualisiert', { closeButton: true, autoClose: 2000 })
  }

  async function handleSortChange(newSort: string) {
    await updateAlbum(album.id, { default_files_order: newSort } as any)
    setAlbum({ ...album, default_files_order: newSort } as Album)
    queryClient.invalidateQueries({ queryKey: ['album', album.id, 'files'] })
    invalidateProject()
    toast('Sortierung aktualisiert', { closeButton: true, autoClose: 2000 })
  }

  async function handleDescriptionSave() {
    setIsSavingDescription(true)
    try {
      await updateAlbum(album.id, { description: descriptionValue } as any)
      invalidateProject()
      descriptionModalRef.current?.close()
      toast('Beschreibung aktualisiert', { closeButton: true, autoClose: 2000 })
    } catch {
      alert('Fehler beim Speichern der Beschreibung')
    } finally {
      setIsSavingDescription(false)
    }
  }

  async function handleDelete() {
    setIsDeletingAlbum(true)
    try {
      await deleteAlbum(album.id)
      invalidateProject()
      toast('Album gelöscht', { closeButton: true, autoClose: 2000 })
    } catch (e: any) {
      if (e?.response?.status === 409) {
        alert('Album enthält noch Bilder und kann nicht gelöscht werden.')
      } else {
        alert('Fehler beim Löschen des Albums')
      }
    } finally {
      setIsDeletingAlbum(false)
    }
  }

  // --- Focal point handling (used by preview tiles AND gallery hover actions) ---

  function openFocalPointForNewFile(file: AlbumFile, type: 'desktop' | 'mobile') {
    setFocalPointImageUrl(file.detail ?? file.file)
    setFocalPointNewFileId(file.id)
    setFocalPointType(type)
    setFocalPointInitial({ x: 0.5, y: 0.5 })
    setTimeout(() => focalPointModalRef.current?.showModal(), 0)
  }

  function openFocalPointForExisting(type: 'desktop' | 'mobile') {
    const previewFile = type === 'desktop' ? album.preview_desktop : album.preview_mobile
    if (!previewFile) return
    setFocalPointImageUrl(previewFile.detail || previewFile.file)
    setFocalPointNewFileId(null)
    setFocalPointType(type)
    const initX = type === 'desktop' ? album.preview_desktop_center_x : album.preview_mobile_center_x
    const initY = type === 'desktop' ? album.preview_desktop_center_y : album.preview_mobile_center_y
    setFocalPointInitial({ x: initX, y: initY })
    setTimeout(() => focalPointModalRef.current?.showModal(), 0)
  }

  async function handleFocalPointConfirm(centerX: number, centerY: number) {
    setIsSavingFocalPoint(true)
    try {
      let data: Record<string, any>
      if (focalPointNewFileId) {
        data = focalPointType === 'desktop'
          ? { preview_desktop: focalPointNewFileId, preview_desktop_center_x: centerX, preview_desktop_center_y: centerY }
          : { preview_mobile: focalPointNewFileId, preview_mobile_center_x: centerX, preview_mobile_center_y: centerY }
      } else {
        data = focalPointType === 'desktop'
          ? { preview_desktop_center_x: centerX, preview_desktop_center_y: centerY }
          : { preview_mobile_center_x: centerX, preview_mobile_center_y: centerY }
      }
      await updateAlbum(album.id, data as any)
      invalidateProject()
      focalPointModalRef.current?.close()
      toast('Cover aktualisiert', { closeButton: true, autoClose: 2000 })
    } catch {
      alert('Fehler beim Speichern des Covers')
    } finally {
      setIsSavingFocalPoint(false)
    }
  }

  async function handleRemovePreview(type: 'desktop' | 'mobile') {
    try {
      const data = type === 'desktop'
        ? { preview_desktop: null }
        : { preview_mobile: null }
      await updateAlbum(album.id, data as any)
      invalidateProject()
      toast('Vorschaubild entfernt', { closeButton: true, autoClose: 2000 })
    } catch {
      alert('Fehler beim Entfernen des Vorschaubilds')
    }
  }

  // Mobile long-press actions for preview tiles
  const [previewLongPress, setPreviewLongPress] = useState<'desktop' | 'mobile' | null>(null)

  function getPreviewActions(type: 'desktop' | 'mobile'): SimpleAction[] {
    return [
      {
        label: 'Fokuspunkt ändern',
        icon: IconFocusCentered,
        onClick: () => openFocalPointForExisting(type),
      },
      {
        label: 'Vorschaubild entfernen',
        icon: IconX,
        onClick: () => handleRemovePreview(type),
        className: 'text-error',
      },
    ]
  }

  return (
    <div className="bg-neutral overflow-hidden border border-[var(--portal-border)]">
      {/* Admin bar */}
      <div className="px-6 py-4 flex items-center gap-3 border-b border-[var(--portal-border)]">
        <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center overflow-hidden flex-shrink-0">
          {album.thumbnail ? (
            <img src={album.thumbnail} alt={album.title} className="w-full h-full object-cover" />
          ) : (
            <IconPhoto size={20} className="opacity-40" />
          )}
        </div>

        <div className="flex-1 min-w-0 flex items-center gap-2">
          <InlineEditInput value={album.title} onSave={handleTitleSave} size="sm" className="min-w-[12rem]">
            <span className="font-serif text-sm truncate block">{album.title}</span>
          </InlineEditInput>
          {album.is_primary && (
            <span
              className="hidden sm:inline text-[10px] uppercase tracking-wider opacity-50 border border-[var(--portal-border)] rounded-full px-2 py-0.5 flex-shrink-0"
              title="Das erste Album ist das Hauptalbum – es wird zuerst geöffnet und liefert das Vorschaubild."
            >
              Hauptalbum
            </span>
          )}
        </div>

        {album.files_count != null && (
          <span className="flex items-center gap-1 text-xs opacity-50 flex-shrink-0" title="Anzahl Bilder">
            <IconPhoto size={14} />
            {album.files_count}
          </span>
        )}

        <div className="flex items-center gap-1 flex-shrink-0">
          {(onMoveUp || onMoveDown) && (
            <div className="flex items-center mr-1">
              <button
                className="btn btn-ghost btn-sm btn-square"
                title="Nach oben verschieben"
                disabled={!onMoveUp || isReordering}
                onClick={onMoveUp}
              >
                <IconArrowUp size={16} className="opacity-40" />
              </button>
              <button
                className="btn btn-ghost btn-sm btn-square"
                title="Nach unten verschieben"
                disabled={!onMoveDown || isReordering}
                onClick={onMoveDown}
              >
                <IconArrowDown size={16} className="opacity-40" />
              </button>
            </div>
          )}

          {!album.is_primary && (
            <button
              className="btn btn-ghost btn-sm btn-square"
              title="Album löschen"
              onClick={deleteModal.show}
            >
              <IconTrash size={16} className="opacity-40 hover:text-error" />
            </button>
          )}

          <button
            className="btn btn-ghost btn-sm btn-square"
            title="Beschreibung bearbeiten"
            onClick={() => {
              setDescriptionValue(album.description)
              descriptionModalRef.current?.showModal()
            }}
          >
            <IconFileDescription size={16} className="opacity-40" />
          </button>

          <select
            className="select select-sm select-bordered min-w-0 w-32 text-xs border-[var(--portal-border)] bg-neutral focus:outline-none"
            value={album.default_files_order || '-created_at'}
            onChange={(e) => handleSortChange(e.target.value)}
          >
            <option value="-created_at">Upload ↓</option>
            <option value="created_at">Upload ↑</option>
            <option value="-file_creation">Aufnahme ↓</option>
            <option value="file_creation">Aufnahme ↑</option>
          </select>
        </div>
      </div>

      {/* Preview tiles – always visible */}
      <div className="p-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <PreviewTile
          label="Desktop"
          previewFile={album.preview_desktop}
          onChangeFocalPoint={() => openFocalPointForExisting('desktop')}
          onRemove={() => handleRemovePreview('desktop')}
          onLongPress={() => album.preview_desktop && setPreviewLongPress('desktop')}
        />
        <PreviewTile
          label="Mobile Geräte"
          previewFile={album.preview_mobile}
          onChangeFocalPoint={() => openFocalPointForExisting('mobile')}
          onRemove={() => handleRemovePreview('mobile')}
          onLongPress={() => album.preview_mobile && setPreviewLongPress('mobile')}
        />
        {albumIsEmpty ? (
          <div className="aspect-[4/3] overflow-hidden flex flex-col gap-1">
            <div
              className={`flex-1 rounded-lg border-2 border-dashed border-[var(--portal-border)] flex flex-col items-center justify-center gap-1 p-2 text-center cursor-pointer hover:border-primary hover:border-opacity-50 transition-colors ${isUploading ? 'pointer-events-none opacity-60' : ''}`}
              onClick={handleUploadClick}
              title="Bilder hinzufügen"
            >
              {isUploading ? (
                <Loading size="sm" color="primary" />
              ) : (
                <IconUpload size={20} className="opacity-30" />
              )}
              <span className="text-xs opacity-40">
                {isUploading ? 'Hochladen...' : 'Bilder & Videos hinzufügen'}
              </span>
            </div>
            <TutorialSpot id="album-load-examples">
              <div
                className={`flex-1 rounded-lg border-2 border-dashed border-[var(--portal-border)] flex flex-col items-center justify-center gap-1 p-2 text-center cursor-pointer hover:border-primary hover:border-opacity-50 transition-colors ${isApplyingExamples ? 'pointer-events-none opacity-60' : ''}`}
                onClick={handleApplyExamplesFromTile}
              >
                {isApplyingExamples ? (
                  <Loading size="sm" color="primary" />
                ) : (
                  <IconPhotoPlus size={20} className="opacity-30" />
                )}
                <span className="text-xs opacity-40">
                  {isApplyingExamples ? 'Laden...' : 'Beispiel-Bilder'}
                </span>
              </div>
            </TutorialSpot>
          </div>
        ) : (
          <div
            className={`aspect-[4/3] border-2 border-dashed border-[var(--portal-border)] flex flex-col items-center justify-center gap-2 p-3 text-center cursor-pointer hover:border-primary hover:border-opacity-50 transition-colors ${isUploading ? 'pointer-events-none opacity-60' : ''}`}
            onClick={handleUploadClick}
            title="Bilder hinzufügen"
          >
            {isUploading ? (
              <Loading size="sm" color="primary" />
            ) : (
              <IconUpload size={20} className="opacity-30" />
            )}
            <span className="text-xs opacity-40">
              {isUploading ? 'Wird hochgeladen...' : 'Bilder & Videos hinzufügen'}
            </span>
          </div>
        )}
        <input
          ref={inputFileRef}
          type="file"
          accept="image/*,video/*"
          multiple
          className="hidden"
          onChange={handleFileSelect}
        />
      </div>

      {/* Mobile long-press modal for preview tiles */}
      <SimpleActionsModal
        open={!!previewLongPress}
        title={previewLongPress === 'desktop' ? 'Desktop-Vorschaubild' : 'Mobile-Vorschaubild'}
        actions={previewLongPress ? getPreviewActions(previewLongPress) : []}
        onClose={() => setPreviewLongPress(null)}
      />

      {/* Expand button for files */}
      <div
        className="px-3 pb-3 flex items-center gap-2 cursor-pointer select-none hover:opacity-80 transition-opacity"
        onClick={() => setIsExpanded(prev => !prev)}
      >
        {isExpanded
          ? <IconChevronDown size={18} className="text-primary" />
          : <IconChevronRight size={18} className="text-primary" />
        }
        <span className="text-sm text-primary font-medium">
          {isExpanded ? 'Bilder ausblenden' : 'Bilder anzeigen'}
        </span>
      </div>

      {/* Files section */}
      {isExpanded && (
        <div className="px-3 pb-3">
          <FileFilterProvider>
            <FilesProvider albumId={album.id}>
              <AlbumFilesGrid
                album={album}
                onSetPreview={openFocalPointForNewFile}
                onAlbumUpdated={invalidateProject}
              />
            </FilesProvider>
          </FileFilterProvider>
        </div>
      )}

      {/* Description modal */}
      <Modal ref={descriptionModalRef} className="bg-neutral">
        <Modal.Header>
          <span className="text-xl font-bold">Beschreibung bearbeiten</span>
          <form method="dialog">
            <button className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5">✕</button>
          </form>
        </Modal.Header>
        <Modal.Body>
          <div className="flex flex-col gap-4">
            <Textarea
              className="w-full"
              rows={4}
              placeholder="Beschreibung des Albums..."
              value={descriptionValue}
              onChange={e => setDescriptionValue(e.target.value)}
            />
            <div className="flex flex-row gap-3 justify-center">
              <form method="dialog">
                <Button color="ghost" disabled={isSavingDescription}>Abbrechen</Button>
              </form>
              <Button
                color="primary"
                onClick={handleDescriptionSave}
                loading={isSavingDescription}
                disabled={isSavingDescription}
              >
                Speichern
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Delete album confirm modal */}
      <DeleteConfirmModal
        modalRef={deleteModal.modalRef}
        onConfirm={handleDelete}
        isLoading={isDeletingAlbum}
        title="Album löschen"
        message="Möchtest du dieses Album wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden."
      />

      {/* Focal point modal (shared) */}
      <FocalPointModal
        modalRef={focalPointModalRef}
        imageUrl={focalPointImageUrl}
        initialX={focalPointInitial.x}
        initialY={focalPointInitial.y}
        onConfirm={handleFocalPointConfirm}
        isLoading={isSavingFocalPoint}
      />
    </div>
  )
}

// --- Preview tile for desktop/mobile cover ---

function PreviewTile({ label, previewFile, onChangeFocalPoint, onRemove, onLongPress }: {
  label: string
  previewFile: AlbumPreviewFile | null
  onChangeFocalPoint: () => void
  onRemove: () => void
  onLongPress: () => void
}) {
  const [hovered, setHovered] = useState(false)
  const imageUrl = previewFile ? (previewFile.detail || previewFile.file) : null

  const longPress = useLongPress(() => {
    if (imageUrl) onLongPress()
  })

  return (
    <div
      className="aspect-[4/3] bg-secondary overflow-hidden relative cursor-default"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      {...longPress.handlers}
    >
      {imageUrl ? (
        <>
          <img
            src={imageUrl}
            alt={label}
            className="w-full h-full object-contain"
            draggable={false}
          />
          {/* Hover actions (desktop only) */}
          <div className={`absolute inset-0 hidden md:flex flex-col justify-between pointer-events-none transition-opacity duration-200 ${hovered ? 'opacity-100' : 'opacity-0'}`}>
            <div className="bg-secondary px-2 py-1 text-base-content text-sm truncate flex items-center gap-1">
              {label}
            </div>
            <div className="flex justify-center gap-1 p-2 pointer-events-auto">
              <button
                title="Fokuspunkt ändern"
                className="btn btn-circle btn-sm bg-secondary hover:bg-neutral border-none text-base-content"
                onClick={onChangeFocalPoint}
              >
                <IconFocusCentered size={16} />
              </button>
              <button
                title="Vorschaubild entfernen"
                className="btn btn-circle btn-sm bg-secondary hover:bg-neutral border-none text-error"
                onClick={onRemove}
              >
                <IconX size={16} />
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="w-full h-full flex items-center justify-center p-2 text-center relative">
          <span className="text-xs md:text-sm opacity-60 leading-tight">Kein Vorschaubild für {label} gesetzt</span>
          {/* Hover hint as overlay */}
          <div className={`absolute inset-0 bg-secondary flex items-center justify-center p-3 text-center transition-opacity duration-200 ${hovered ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <span className="text-xs md:text-sm opacity-70 leading-tight">
              Über ein Bild in der Galerie hovern, um es als Cover zu setzen
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

// --- Files grid (no preview tiles, no focal point – those are in AlbumCard) ---

// Default backend page size for album files — used to label the "load more" button
const FILES_PAGE_SIZE = 50

// View options of the file grid are remembered across reloads (per browser, not per album).
const DISPLAY_MODE_STORAGE_KEY = 'heyalbum:portal:filesDisplayMode'
const SHOW_FILE_NAMES_STORAGE_KEY = 'heyalbum:portal:filesShowNames'

function readStoredDisplayMode(): 'cover' | 'contain' {
  return localStorage.getItem(DISPLAY_MODE_STORAGE_KEY) === 'cover' ? 'cover' : 'contain'
}

function readStoredShowFileNames(): boolean {
  return localStorage.getItem(SHOW_FILE_NAMES_STORAGE_KEY) === '1'
}

function AlbumFilesGrid({ album, onSetPreview, onAlbumUpdated }: {
  album: Album & { files_count?: number }
  onSetPreview: (file: AlbumFile, type: 'desktop' | 'mobile') => void
  onAlbumUpdated: () => void
}) {
  const filesContext = useFilesContext()
  const [selectedFileIndex, setSelectedFileIndex] = useState<number | null>(null)
  const fileSelection = useFileSelection()
  const { isSelecting, setIsSelecting } = fileSelection
  const [displayMode, setDisplayMode] = useState<'cover' | 'contain'>(readStoredDisplayMode)
  const [showFileNames, setShowFileNames] = useState<boolean>(readStoredShowFileNames)
  const [wasInitiallyEmpty, setWasInitiallyEmpty] = useState<boolean | null>(null)
  const [isApplyingExamples, setIsApplyingExamples] = useState(false)
  const [isLoadingAllFiles, setIsLoadingAllFiles] = useState(false)

  // Delete file modal state
  const deleteFileModal = useDeleteConfirmModal()
  const [fileToDelete, setFileToDelete] = useState<AlbumFile | null>(null)
  const [isDeletingFile, setIsDeletingFile] = useState(false)

  // Bulk action state
  const { deleteFile, deleteFiles, updateFiles, updateAlbum: updateAlbumClient, applyFileDeclaration } = useClient()
  const { removeUploadedFilesForAlbum } = useUploadQueue()
  const queryClient = useQueryClient()
  const { currentDrive, drives } = useDrives()
  const bulkDeleteModal = useDeleteConfirmModal()
  const bulkMoveModalRef = useRef<HTMLDialogElement>(null)
  const bulkDownloadModalRef = useRef<HTMLDialogElement>(null)
  const [isBulkDeleting, setIsBulkDeleting] = useState(false)
  const [isBulkMoving, setIsBulkMoving] = useState(false)
  const { startDownload, isDownloading, progress: downloadProgress } = useMultiDownload()

  // Thumbnail crop state
  const cropModalRef = useRef<HTMLDialogElement>(null)
  const cropImgRef = useRef<HTMLImageElement>(null)
  const [cropBlobUrl, setCropBlobUrl] = useState<string | null>(null)
  const [cropZoom, setCropZoom] = useState(100) // 20-100: % of shorter side the square covers
  const [cropPos, setCropPos] = useState({ x: 50, y: 50 }) // square center as % of image dims
  const [cropNatural, setCropNatural] = useState({ w: 1, h: 1 })
  const [cropReady, setCropReady] = useState(false)
  const [isSavingThumbnail, setIsSavingThumbnail] = useState(false)
  const isDraggingRef = useRef(false)
  const dragStartRef = useRef({ x: 0, y: 0, px: 0, py: 0 })

  if (!filesContext) return null

  const { currentFilesCollection, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage, totalCount, loadedCount } = filesContext

  // Track if album was initially empty (only set once after first load)
  if (wasInitiallyEmpty === null && !isLoading && currentFilesCollection) {
    setWasInitiallyEmpty(currentFilesCollection.files.length === 0)
  }

  // "Load more" / "load all" counters. Prefer the server totals; fall back to the album count.
  const totalFilesCount = totalCount ?? album.files_count ?? (currentFilesCollection?.files.length ?? 0)
  const loadedFilesCount = loadedCount ?? (currentFilesCollection?.files.length ?? 0)
  const remainingFilesCount = Math.max(0, totalFilesCount - loadedFilesCount)
  const nextBatchCount = Math.min(FILES_PAGE_SIZE, remainingFilesCount || FILES_PAGE_SIZE)

  // Load all remaining files by paging through until the cursor is exhausted.
  async function handleLoadAllFiles() {
    setIsLoadingAllFiles(true)
    try {
      let result = await fetchNextPage()
      while (result.hasNextPage) {
        result = await fetchNextPage()
      }
    } finally {
      setIsLoadingAllFiles(false)
    }
  }

  async function handleApplyExamples() {
    setIsApplyingExamples(true)
    try {
      await applyFileDeclaration(album.id, 'prof')
      onAlbumUpdated()
      queryClient.invalidateQueries({ queryKey: ['album', album.id, 'files'] })
      setWasInitiallyEmpty(false)
      toast('Beispiel-Bilder wurden geladen', { closeButton: true, autoClose: 2000 })
    } catch {
      toast('Fehler beim Laden der Beispiel-Bilder', { closeButton: true, autoClose: 3000, type: 'error' })
    } finally {
      setIsApplyingExamples(false)
    }
  }

  // --- Bulk action handlers ---

  const selectedFiles = fileSelection.getSelectedAlbumFileIds()
  const isAllSelected = fileSelection.isAllSelected(album.id)
  const hasSelection = isAllSelected || selectedFiles.length > 0

  async function handleBulkDelete() {
    let filter: Array<Array<string>>
    try {
      filter = getFilterFromSelection(fileSelection, album.id)
    } catch {
      return
    }
    setIsBulkDeleting(true)
    try {
      await deleteFiles(album, filter)
      queryClient.invalidateQueries({ queryKey: makeKey(album.id) })
      removeUploadedFilesForAlbum(album)
      onAlbumUpdated()
      setIsSelecting(false)
      bulkDeleteModal.close()
      toast('Dateien gelöscht', { closeButton: true, autoClose: 2000 })
    } catch (e: any) {
      alert('Fehler beim Löschen: ' + JSON.stringify(e.response?.data))
    } finally {
      setIsBulkDeleting(false)
    }
  }

  async function handleBulkMove(targetAlbum: Album) {
    let filter: Array<Array<string>>
    try {
      filter = getFilterFromSelection(fileSelection, album.id)
    } catch {
      return
    }
    setIsBulkMoving(true)
    try {
      await updateFiles(album, filter, { album: targetAlbum.id })
      queryClient.invalidateQueries({ queryKey: makeKey(album.id) })
      queryClient.invalidateQueries({ queryKey: makeKey(targetAlbum.id) })
      removeUploadedFilesForAlbum(album)
      onAlbumUpdated()
      setIsSelecting(false)
      bulkMoveModalRef.current?.close()
      toast('Dateien verschoben', { closeButton: true, autoClose: 2000 })
    } catch (e: any) {
      alert('Fehler beim Verschieben: ' + JSON.stringify(e.response?.data))
    } finally {
      setIsBulkMoving(false)
    }
  }

  async function handleBulkDownload(quality: DownloadQuality) {
    if (!currentDrive) return
    const albumIds: string[] = isAllSelected ? [album.id] : []
    const fileIds: string[] = isAllSelected ? [] : selectedFiles
    try {
      await startDownload(currentDrive.id, albumIds, fileIds, quality, () => {
        bulkDownloadModalRef.current?.close()
      })
    } catch {
      alert('Fehler beim Erstellen des Downloads')
    }
  }

  const otherAlbums = currentDrive ? drives[currentDrive.id]?.albums.filter(a => a.id !== album.id) : []

  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <Loading size="md" color="primary" />
      </div>
    )
  }

  if (!currentFilesCollection || currentFilesCollection.files.length === 0) {
    return (
      <div className="py-4 text-center">
        <p className="opacity-60 mb-3">Keine Bilder vorhanden</p>
        {wasInitiallyEmpty && (
          <Button
            size="sm"
            color="primary"
            variant="outline"
            onClick={handleApplyExamples}
            loading={isApplyingExamples}
            disabled={isApplyingExamples}
          >
            <IconPhotoPlus size={18} />
            Beispiel-Bilder laden
          </Button>
        )}
      </div>
    )
  }

  function onFileClick(file: AlbumFile) {
    const index = currentFilesCollection!.files.findIndex(f => f.id === file.id)
    if (index !== -1) {
      setSelectedFileIndex(index)
    }
  }

  function handleDownload(file: AlbumFile) {
    const url = `${import.meta.env.VITE_PUBLIC_API_URL}/api/files/${file.id}/download/`
    saveAs(url, file.file_name, () => {})
  }

  function handleDeleteFile(file: AlbumFile) {
    setFileToDelete(file)
    deleteFileModal.show()
  }

  async function confirmDeleteFile() {
    if (!fileToDelete) return
    setIsDeletingFile(true)
    try {
      await deleteFile(fileToDelete)
      queryClient.invalidateQueries({ queryKey: ['album', album.id, 'files'] })
      deleteFileModal.close()
      toast('Datei gelöscht', { closeButton: true, autoClose: 2000 })
    } catch {
      alert('Fehler beim Löschen der Datei')
    } finally {
      setIsDeletingFile(false)
    }
  }

  // Clamp crop position so the square stays within the image
  function clampCropPos(x: number, y: number, zoom: number, nat: { w: number, h: number }) {
    const halfX = zoom * Math.min(1, nat.h / nat.w) / 2
    const halfY = zoom * Math.min(1, nat.w / nat.h) / 2
    return {
      x: Math.min(100 - halfX, Math.max(halfX, x)),
      y: Math.min(100 - halfY, Math.max(halfY, y)),
    }
  }

  async function handleSetThumbnail(file: AlbumFile) {
    const url = file.detail ?? file.file
    setCropZoom(100)
    setCropPos({ x: 50, y: 50 })
    setCropReady(false)
    // Fetch as blob to avoid CORS issues when drawing to canvas later
    try {
      const res = await fetch(url, { cache: 'no-cache' })
      const blob = await res.blob()
      setCropBlobUrl(URL.createObjectURL(blob))
    } catch {
      setCropBlobUrl(null)
    }
    setTimeout(() => cropModalRef.current?.showModal(), 0)
  }

  function handleCropImgLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const img = e.currentTarget
    setCropNatural({ w: img.naturalWidth, h: img.naturalHeight })
    setCropReady(true)
  }

  function handleCropPointerDown(e: React.PointerEvent) {
    isDraggingRef.current = true
    dragStartRef.current = { x: e.clientX, y: e.clientY, px: cropPos.x, py: cropPos.y }
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  }

  function handleCropPointerMove(e: React.PointerEvent) {
    if (!isDraggingRef.current) return
    const imgEl = cropImgRef.current
    if (!imgEl) return
    const rw = imgEl.clientWidth
    const rh = imgEl.clientHeight
    // Convert pixel drag to percentage of image dimensions
    const dx = (e.clientX - dragStartRef.current.x) / rw * 100
    const dy = (e.clientY - dragStartRef.current.y) / rh * 100
    const clamped = clampCropPos(dragStartRef.current.px + dx, dragStartRef.current.py + dy, cropZoom, cropNatural)
    setCropPos(clamped)
  }

  function handleCropPointerUp() {
    isDraggingRef.current = false
  }

  async function confirmCropThumbnail() {
    if (!cropBlobUrl) return
    setIsSavingThumbnail(true)
    try {
      const img = new Image()
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve()
        img.onerror = reject
        img.src = cropBlobUrl
      })

      const nw = img.naturalWidth
      const nh = img.naturalHeight
      // Square size in natural pixels: cropZoom% of the shorter side
      const shorter = Math.min(nw, nh)
      const cropSize = (cropZoom / 100) * shorter
      // Square center in natural pixels
      const cx = (cropPos.x / 100) * nw
      const cy = (cropPos.y / 100) * nh
      const offsetX = cx - cropSize / 2
      const offsetY = cy - cropSize / 2

      const canvas = document.createElement('canvas')
      canvas.width = 512
      canvas.height = 512
      const ctx = canvas.getContext('2d')!
      ctx.drawImage(img, offsetX, offsetY, cropSize, cropSize, 0, 0, 512, 512)

      const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.9))
      if (!blob) return
      const formData = new FormData()
      formData.append('thumbnail', new File([blob], 'thumbnail.jpg', { type: 'image/jpeg' }))
      await updateAlbumClient(album.id, formData)
      cropModalRef.current?.close()
      URL.revokeObjectURL(cropBlobUrl)
      setCropBlobUrl(null)
      onAlbumUpdated()
      toast('Thumbnail aktualisiert', { closeButton: true, autoClose: 2000 })
    } catch {
      toast('Fehler beim Setzen des Thumbnails', { closeButton: true, autoClose: 3000, type: 'error' })
    } finally {
      setIsSavingThumbnail(false)
    }
  }

  // Covers and the album thumbnail are rendered as <img>, so they only work with images
  const isImage = (file: AlbumFile) => file.file_type === AlbumFileType.IMAGE

  const fileActions: FileAction[] = [
    {
      label: 'Desktop-Cover',
      icon: IconDeviceDesktop,
      onClick: (file) => onSetPreview(file, 'desktop'),
      isAvailable: isImage,
    },
    {
      label: 'Mobile-Cover',
      icon: IconDeviceMobile,
      onClick: (file) => onSetPreview(file, 'mobile'),
      isAvailable: isImage,
    },
    {
      label: 'Vorschaubild setzen',
      icon: IconPhotoCheck,
      onClick: handleSetThumbnail,
      isAvailable: isImage,
    },
    {
      label: 'Herunterladen',
      icon: IconDownload,
      onClick: handleDownload,
    },
    {
      label: 'Löschen',
      icon: IconTrash,
      onClick: handleDeleteFile,
      className: 'text-error',
    },
  ]

  return (
    <div className="mt-1">
      <div className="flex justify-end mb-2 gap-2">
        <Button
          className={`rounded-full ${showFileNames ? 'bg-primary bg-opacity-15 text-primary' : ''}`}
          size="sm"
          color="ghost"
          onClick={() => {
            const next = !showFileNames
            setShowFileNames(next)
            localStorage.setItem(SHOW_FILE_NAMES_STORAGE_KEY, next ? '1' : '0')
          }}
          title={showFileNames ? 'Dateinamen ausblenden' : 'Dateinamen immer anzeigen'}
        >
          <IconFileText size={18} />
        </Button>
        <Button
          className={`rounded-full ${displayMode === 'cover' ? 'bg-primary bg-opacity-15 text-primary' : ''}`}
          size="sm"
          color="ghost"
          onClick={() => {
            const next = displayMode === 'cover' ? 'contain' : 'cover'
            setDisplayMode(next)
            localStorage.setItem(DISPLAY_MODE_STORAGE_KEY, next)
          }}
          title={displayMode === 'cover' ? 'Vollständig anzeigen' : 'Zugeschnitten anzeigen'}
        >
          <IconBoxMultiple size={18} />
        </Button>
        <Button
          className="rounded-full"
          size="sm"
          color="primary"
          onClick={() => setIsSelecting(!isSelecting)}
        >
          {isSelecting ? 'abbrechen' : 'auswählen'}
        </Button>
      </div>

      {/* Selection action bar */}
      {isSelecting && (
        <div className="flex items-center gap-2 mb-2 p-2 bg-secondary rounded">
          <Button
            fullRounded
            className="border-2"
            variant={isAllSelected ? undefined : 'outline'}
            color="primary"
            size="sm"
            onClick={() => fileSelection.setIsAllSelected(album.id, !isAllSelected)}
          >
            {isAllSelected ? <IconCheck size={16} /> : null}
            Alle
          </Button>
          <span className="text-sm text-primary font-medium flex-1 text-center">
            {isAllSelected ? 'Alle ausgewählt' : selectedFiles.length > 0 ? `${selectedFiles.length} ausgewählt` : 'Bilder auswählen'}
          </span>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              color="primary"
              variant="outline"
              className="px-2 border-0"
              disabled={!hasSelection}
              onClick={() => bulkDownloadModalRef.current?.showModal()}
              title="Herunterladen"
            >
              <IconDownload size={18} />
            </Button>
            {otherAlbums.length > 0 && (
              <Button
                size="sm"
                color="primary"
                variant="outline"
                className="px-2 border-0"
                disabled={!hasSelection}
                onClick={() => bulkMoveModalRef.current?.showModal()}
                title="Verschieben"
              >
                <IconArrowsMove size={18} />
              </Button>
            )}
            <Button
              size="sm"
              color="error"
              variant="outline"
              className="px-2 border-0"
              disabled={!hasSelection}
              onClick={bulkDeleteModal.show}
              title="Löschen"
            >
              <IconTrash size={18} />
            </Button>
          </div>
        </div>
      )}

      <FilesListing
        files={currentFilesCollection}
        albumId={album.id}
        fileSelection={fileSelection}
        onFileClick={isSelecting ? fileSelection.toggleFileSelection : onFileClick}
        fileActions={isSelecting ? undefined : fileActions}
        displayMode={displayMode}
        showFileNames={showFileNames}
      />

      {/* Load more / load all — only shown while the server has more files than are loaded */}
      {hasNextPage && (
        <div className="flex flex-wrap justify-center gap-3 pt-6">
          <Button
            color="primary"
            variant="outline"
            size="sm"
            loading={isFetchingNextPage && !isLoadingAllFiles}
            disabled={isFetchingNextPage || isLoadingAllFiles}
            onClick={() => fetchNextPage()}
          >
            {nextBatchCount} weitere Bilder laden
          </Button>
          {remainingFilesCount > nextBatchCount && (
            <Button
              color="primary"
              size="sm"
              loading={isLoadingAllFiles}
              disabled={isFetchingNextPage || isLoadingAllFiles}
              onClick={handleLoadAllFiles}
            >
              Alle {totalFilesCount} Bilder laden
            </Button>
          )}
        </div>
      )}

      <FilesSlideshow
        files={currentFilesCollection.files}
        selectedFileIndex={selectedFileIndex}
        onSelectFileIndex={setSelectedFileIndex}
      />

      {/* Delete file confirm modal */}
      <DeleteConfirmModal
        modalRef={deleteFileModal.modalRef}
        onConfirm={confirmDeleteFile}
        isLoading={isDeletingFile}
        title="Datei löschen"
        message="Möchtest du diese Datei wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden."
      />

      {/* Bulk delete modal */}
      <DeleteConfirmModal
        modalRef={bulkDeleteModal.modalRef}
        onConfirm={handleBulkDelete}
        isLoading={isBulkDeleting}
        title="Dateien löschen"
        message={isAllSelected ? 'Alle Bilder in diesem Album löschen?' : `${selectedFiles.length} ausgewählte Bilder löschen?`}
      />

      {/* Bulk move modal */}
      <BulkMoveModal
        modalRef={bulkMoveModalRef}
        availableAlbums={otherAlbums}
        onConfirm={handleBulkMove}
        isLoading={isBulkMoving}
      />

      {/* Bulk download modal */}
      <Modal ref={bulkDownloadModalRef} className="bg-neutral">
        <Modal.Header>
          <div className="flex flex-row gap-2 justify-center">
            <div className="bg-primary text-neutral rounded-full p-1">
              <IconDownload size={20} />
            </div>
            Download
          </div>
          <form method="dialog">
            <button disabled={isDownloading} className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5">✕</button>
          </form>
        </Modal.Header>
        <Modal.Body>
          {isDownloading ? (
            <div className="flex flex-col gap-2 items-center">
              <DownloadProgressBar progress={downloadProgress} label="Download wird erstellt" />
            </div>
          ) : (
            <div className="flex flex-col gap-3 items-center">
              {/* The photographer's own portal view is never restricted by the drive's
                  download_options — that setting only governs what guests are offered. */}
              <DownloadQualityOptions
                qualities={ALL_DOWNLOAD_QUALITIES}
                onSelect={handleBulkDownload}
              />
              <form method="dialog">
                <Button className="mt-3" color="ghost">Abbrechen</Button>
              </form>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Thumbnail crop modal */}
      <Modal ref={cropModalRef} className="bg-neutral max-w-lg">
        <Modal.Header>
          <span className="text-xl font-bold">Thumbnail zuschneiden</span>
          <form method="dialog">
            <button className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5">✕</button>
          </form>
        </Modal.Header>
        <Modal.Body>
          <div className="flex flex-col gap-4 items-center">
            {cropBlobUrl && (() => {
              const imgEl = cropImgRef.current
              const rw = imgEl?.clientWidth || 0
              const rh = imgEl?.clientHeight || 0
              const shorterR = Math.min(rw, rh)
              const sqSize = (cropZoom / 100) * shorterR
              const sqLeft = (cropPos.x / 100) * rw - sqSize / 2
              const sqTop = (cropPos.y / 100) * rh - sqSize / 2
              return (
                <div
                  className="relative inline-block cursor-move select-none"
                  onPointerDown={handleCropPointerDown}
                  onPointerMove={handleCropPointerMove}
                  onPointerUp={handleCropPointerUp}
                >
                  <img
                    ref={cropImgRef}
                    src={cropBlobUrl}
                    alt=""
                    className="max-w-full max-h-[50vh] block rounded"
                    draggable={false}
                    onLoad={handleCropImgLoad}
                  />
                  {cropReady && (
                    <>
                      {/* Dark overlay with square cutout */}
                      <div className="absolute inset-0 pointer-events-none" style={{
                        background: 'rgba(0,0,0,0.5)',
                        clipPath: `polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 0, ${sqLeft}px ${sqTop}px, ${sqLeft}px ${sqTop + sqSize}px, ${sqLeft + sqSize}px ${sqTop + sqSize}px, ${sqLeft + sqSize}px ${sqTop}px, ${sqLeft}px ${sqTop}px)`,
                      }} />
                      {/* Dashed selection border */}
                      <div className="absolute border-2 border-dashed border-[white] pointer-events-none rounded-sm" style={{
                        left: sqLeft, top: sqTop, width: sqSize, height: sqSize,
                      }} />
                    </>
                  )}
                </div>
              )
            })()}
            <div className="flex items-center gap-3 w-full">
              <button className="btn btn-ghost btn-sm btn-circle" onClick={() => {
                const z = Math.max(20, cropZoom - 10)
                setCropZoom(z)
                setCropPos(prev => clampCropPos(prev.x, prev.y, z, cropNatural))
              }}>
                <IconZoomIn size={20} />
              </button>
              <input
                type="range"
                className="range range-primary range-sm flex-1"
                min={20}
                max={100}
                value={cropZoom}
                onChange={e => {
                  const z = Number(e.target.value)
                  setCropZoom(z)
                  setCropPos(prev => clampCropPos(prev.x, prev.y, z, cropNatural))
                }}
              />
              <button className="btn btn-ghost btn-sm btn-circle" onClick={() => {
                const z = Math.min(100, cropZoom + 10)
                setCropZoom(z)
                setCropPos(prev => clampCropPos(prev.x, prev.y, z, cropNatural))
              }}>
                <IconZoomOut size={20} />
              </button>
            </div>
            <Button color="primary" className="w-full" onClick={confirmCropThumbnail} loading={isSavingThumbnail} disabled={isSavingThumbnail}>
              Speichern
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  )
}
